/* eslint-disable react/prop-types */
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useQuiz } from "src/contexts/QuizContext.js";
import tickThick from "src/files/tick-thick.svg";
import QuizConfirmAnswers from "../../QuizConfirmAnswers/jsx/index.jsx";
import { getAnswers } from "../../QuizHelpers/index.js";
import { quizInteractionTracking } from "src/modules/QuizHelpers/index.js";

function QuizHeaderResponse({ response, setTransition }) {
  const [isHidden, setIsHidden] = useState(setTransition);
  const [useHeight, setUseHeight] = useState(false);

  const itemContRef = useRef(null);

  const calculateItemContHeight = useCallback(() => {
    if (itemContRef.current && !useHeight) {
      setUseHeight(true);
    }
  });

  const disableUseHeight = useCallback(() => {
    setUseHeight(false);
  });

  useEffect(() => {
    if (setTransition) {
      setTimeout(() => {
        setIsHidden(false);
      }, 100);
    }
  }, [setTransition]);

  useEffect(() => {
    if (itemContRef.current) {
      calculateItemContHeight();
    }
  }, [itemContRef.current]);

  useEffect(() => {
    if (!useHeight) {
      calculateItemContHeight();
    }
  }, [useHeight]);

  useEffect(() => {
    window.addEventListener("resize", disableUseHeight);

    return () => {
      window.removeEventListener("resize", disableUseHeight);
    };
  }, []);

  return (
    <div
      className={`quiz-question__header-responses-item ${response.savedAnswer ? `user--saved` : `user--pending`} ${isHidden ? "item--hide" : "item--show"}`}
      style={
        itemContRef.current && useHeight
          ? isHidden
            ? { height: `0px` }
            : { height: `max-content` }
          : {}
      }
      ref={itemContRef}
    >
      <p className="quiz-question__header-response-q">
        {response.title} {response.savedAnswer}
      </p>
    </div>
  );
}

const SavedResponses = ({ _question }) => {
  const responses = getAnswers(_question);
  const { dispatch } = useQuiz();
  const renderResponses = useMemo(() => {
    return responses.map((response, index) => {
      return (
        <QuizHeaderResponse
          key={index}
          response={response}
          setTransition={index === responses.length - 1}
        />
      );
    });
  }, [responses]);

  const qHeaderResponsesRef = useRef();
  // save the ref
  useEffect(() => {
    dispatch({
      type: "qHeaderResponsesRef",
      payload: qHeaderResponsesRef,
    });
  }, []);

  return (
    <div className="quiz-question__header-responses" ref={qHeaderResponsesRef}>
      {renderResponses}
    </div>
  );
};

function RenderHeader({ _question }) {
  const {
    dispatch,
    questionNo,
    questionSection,
    quizStatus,
    windowWidth,
    snapshot,
    questions,
  } = useQuiz();

  const getCurrentAnswerIndex = () => {
    if (!_question.id || !questionSection) return null;
    const qKey = `question${_question.id}`;
    const sKey = `section${questionSection}`;
    const question = questions[qKey];
    if (!question) return null;

    const subquestion = question.subquestions?.[sKey];
    if (!subquestion) return null;

    const currentAnswer =
      subquestion.currentAnswer || subquestion.savedAnswer;
    if (!currentAnswer) return null;

    const options = subquestion.options || [];
    const index = options.findIndex((opt) => opt === currentAnswer);
    return index >= 0 ? index + 1 : null; // 1-based index
  };

  const handleContinueButton = (e) => {
    e.preventDefault();
    
    // Get question number from questionKey (e.g., "question3" -> "Q3")
    const questionNumber = `question${_question.id}`.replace("question", "Q");

    // Track continue quiz button click
    quizInteractionTracking({
      interaction_type: "click",
      question_number: questionNumber,
      sub_question_index: questionSection || null,
      selected_answer_index: getCurrentAnswerIndex(),
      answer_changed: false,
      navigation_action: "continue",
    });

    // update the status
    dispatch({
      type: "continueQuiz",
      payload: {
        questionNo: _question.id,
        questionKey: `question${_question.id}`,
        questionSection,
        snapshot,
      },
    });
  };

  const displayHeader = () => {
    // edit mode
    if (quizStatus === "edit" && _question.id === questionNo) {
      return true;
    }
    // progress mode
    if (
      quizStatus == "progress" ||
      (quizStatus == "start" && _question.id <= questionNo)
    ) {
      return true;
    }
    // summary
    if (quizStatus == "summary") {
      return true;
    }
    return false;
  };
  // determine the style of the displayed header (current or completed)
  // if we are editing, always display as white. ie; current
  const headerClass = () => {
    return _question.status == "completed" && quizStatus !== "edit";
  };
  const displayTitle = () => {
    return _question.id == questionNo || quizStatus === "summary";
  };
  const displaySummaryTitle = () => {
    const keys = Object.keys(_question.subquestions).length;

    const desktop =
      windowWidth > 1024 &&
      keys > 1 &&
      _question.id == questionNo &&
      _question.status !== "confirm" &&
      _question.status !== "summary";

    const mobile = windowWidth <= 1024 && _question.status !== "confirm";

    // only on desktop, mobile always
    return desktop || mobile;
  };

  const renderTitle = () => {
    const regex = /(\(.*\))/i;
    const parts = _question.title.match(regex);
    return {
      title: parts ? _question.title.replace(regex, "") : _question.title,
      subtitle: parts ? parts[0] : "",
    };
  };

  const displayConfirmAnswers = () => {
    // always show on summary
    if (quizStatus === "summary") return true;

    // if mobile view and confirming answers
    if (
      windowWidth <= 1024 &&
      quizStatus !== "summary" &&
      _question.status === "confirm"
    )
      return true;
  };

  //console.log(paragraph.replace(regex, "ferret"));
  return (
    <div
      className={`quiz-question__header-wrapper ${headerClass() ? `question--completed` : `question--current`}`}
    >
      {_question.status == "progress" ? (
        <p className="quiz-question__expanded-progress">In Progress</p>
      ) : (
        <></>
      )}
      <div className="inverse-corner top right turn-0"></div>
      {displayHeader() && (
        <div className="quiz-question__header">
          <div className="inverse-corner top right turn-90 top-offset header--corner"></div>
          <div className="quiz-question__header-no">
            {_question.status == "completed" ? (
              <span className="quiz-question__icon-wrapper">
                <img
                  className="quiz-question__icon-tick"
                  src={tickThick}
                  alt=""
                />
              </span>
            ) : (
              ``
            )}
            Question {_question.sequence}
          </div>

          {displayTitle() && (
            <>
              <h4 className="quiz-question__header-title">
                {renderTitle().title}
              </h4>
            </>
          )}

          {renderTitle().subtitle && (
            <span className="quiz-question__header-title--subtitle">
              {renderTitle().subtitle}
            </span>
          )}

          {/* Responses */}
          {displaySummaryTitle() && <SavedResponses _question={_question} />}

          {/* Confirm the asnwers */}
          {displayConfirmAnswers() && (
            <QuizConfirmAnswers
              _question={_question}
              answerType="savedAnswer"
            />
          )}

          {_question.status == "completed" ? (
            ``
          ) : (
            <button
              type="button"
              className="quiz-question__continue-button"
              onClick={(e) => handleContinueButton(e)}
            >
              Continue
            </button>
          )}
          {_question.status == "completed" ? (
            ``
          ) : (
            <button
              type="button"
              className="quiz-question__cancel-button"
              onClick={(e) => handleContinueButton(e)}
            >
              Cancel
            </button>
          )}
          <div className="inverse-corner bottom right turn-270 top-offset header--corner"></div>
        </div>
      )}
    </div>
  );
}

function RenderHeaderQuestions() {
  const { questions, questionNo } = useQuiz();
  const [renderedHeaders, setRenderedHeaders] = useState([]);
  const qKeys = useMemo(() => Object.keys(questions), [questions]);

  useEffect(() => {
    const newHeaders = [];

    qKeys.forEach((qKey) => {
      const q = questions[qKey];

      if (q.status) {
        const props = {
          _question: q,
        };

        newHeaders.push(<RenderHeader {...props} />);
      }
    });

    setRenderedHeaders(newHeaders);
  }, [questionNo]);

  return renderedHeaders.map((component, index) => (
    <React.Fragment key={index}>{component}</React.Fragment>
  ));
}

function QuizQuestionHeader() {
  return <RenderHeaderQuestions />;
}

export default QuizQuestionHeader;
